import { useState } from 'react';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CardSection from '@shared/components/CardSection';
import DataTable, { type DataTableColumn } from '@shared/components/DataTable';
import PanelSkeleton from '@shared/components/PanelSkeleton';
import RetryableError from '@shared/components/RetryableError';
import { ApiRequestError } from '@api/client/apiClient';
import { useConnectivityStore } from '@shared/hooks/useConnectivityStore';
import { useOrder } from '../hooks/useOrder';
import { useCancelOrder } from '../hooks/useCancelOrder';
import type { OrderItem, OrderStatus } from '../types';

const statusColorMap: Record<OrderStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  Sent: 'default',
  Processing: 'info',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const CANCELLABLE_STATUSES: OrderStatus[] = ['Sent', 'Processing'];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const itemColumns: DataTableColumn<OrderItem>[] = [
  { id: 'productId', label: 'Product ID', width: 140, render: (row) => row.productId },
  { id: 'name', label: 'Name', render: (row) => row.name },
  { id: 'type', label: 'Type', width: 70, render: (row) => row.productType },
  {
    id: 'license',
    label: 'License',
    width: 80,
    render: (row) => (row.licensePeriodMonths ? `${row.licensePeriodMonths}mo` : '—'),
  },
];

interface Props {
  orderId: string;
  onBack: () => void;
}

export default function OrderDetail({ orderId, onBack }: Props) {
  const { data: order, isLoading, error, refetch } = useOrder(orderId);
  const cancelOrder = useCancelOrder();
  const isOffline = useConnectivityStore((s) => s.state) !== 'online';
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) return <PanelSkeleton />;

  if (error || !order) {
    const correlationId =
      error instanceof ApiRequestError ? error.apiError.correlationId : undefined;
    return (
      <RetryableError
        message={error?.message ?? 'Failed to load order'}
        correlationId={correlationId}
        onRetry={() => void refetch()}
      />
    );
  }

  const canCancel = CANCELLABLE_STATUSES.includes(order.status) && !isOffline;

  async function handleCancel() {
    await cancelOrder.mutateAsync(orderId);
    setConfirmOpen(false);
    onBack();
  }

  return (
    <div data-testid="order-detail" className="flex flex-col gap-2">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {order.orderId}
        </Typography>
        <Chip label={order.status} color={statusColorMap[order.status]} size="small" />
      </Box>

      <CardSection title="Details">
        <Box
          component="dl"
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 0.5,
            m: 0,
            '& dt': { color: 'text.secondary', fontWeight: 500 },
          }}
        >
          {order.name && (
            <>
              <dt>Name</dt>
              <dd>{order.name}</dd>
            </>
          )}
          <dt>Source</dt>
          <dd>{order.source}</dd>
          <dt>Created</dt>
          <dd>{formatDate(order.createdAt)}</dd>
          <dt>Created by</dt>
          <dd>{order.createdByLabel}</dd>
          {order.comment && (
            <>
              <dt>Comment</dt>
              <dd>{order.comment}</dd>
            </>
          )}
        </Box>
      </CardSection>

      <CardSection title="Items">
        <DataTable
          columns={itemColumns}
          rows={order.items}
          rowKey={(row) => row.productId}
          emptyMessage="No items"
        />
      </CardSection>

      {canCancel && (
        <Button
          variant="outlined"
          color="error"
          onClick={() => setConfirmOpen(true)}
          disabled={cancelOrder.isPending}
          sx={{ alignSelf: 'flex-start' }}
        >
          Cancel Order
        </Button>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel order {order.orderId}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>No</Button>
          <Button
            color="error"
            onClick={() => void handleCancel()}
            disabled={cancelOrder.isPending}
          >
            Yes, cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
